(function () {
  'use strict';

  if (window.__aovCartLabelSyncLoaded) return;
  window.__aovCartLabelSyncLoaded = true;

  const DISCOUNT_TARGET_SELECTOR = '.AOV-CDMinimal__FooterAction_Options__Discount';
  const HEADER_TARGET_SELECTOR = '.AOV-CDMinimal__Header__Title--start';
  const FOOTER_OPTIONS_SELECTOR = '[class*="AOV-CDMinimal__FooterAction_Options"]';
  const CART_MUTATION_PATTERN = /\/cart\/(update|add|change)(\.js)?/i;
  const AOV_CART_FETCH_PATTERN = /\/cart\.js\?[^#]*app=aov-cart-drawer/i;
  const DISCOUNT_REQUEST_PATTERN = /discount|coupon|promo/i;
  const DEBOUNCE_MS = 150;
  const DISCOUNT_VERIFY_DELAYS_MS = [350, 900];

  let debounceTimer = null;
  let discountVerifyTimers = [];
  let inFlightCartRequest = null;
  let lastKnownDiscountCount = null;
  let lastKnownItemCount = null;
  let isUpdatingDiscount = false;
  let isUpdatingHeader = false;

  function walkDeepElements(root, visit) {
    const start = root || document.body;
    if (!start) return;

    const stack = [start];
    const seen = new Set();

    while (stack.length) {
      const node = stack.pop();
      if (!node || node.nodeType !== 1 || seen.has(node)) continue;
      seen.add(node);

      visit(node);

      if (node.shadowRoot) {
        walkDeepElements(node.shadowRoot, visit);
        continue;
      }

      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]);
      }
    }
  }

  function findAovLabel(selector) {
    let found = null;

    walkDeepElements(document.body, (node) => {
      if (!found && node.matches?.(selector)) {
        found = node;
      }
    });

    return found;
  }

  function getRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function getDiscountElement() {
    return findAovLabel(DISCOUNT_TARGET_SELECTOR);
  }

  function getHeaderElement() {
    return findAovLabel(HEADER_TARGET_SELECTOR);
  }

  function isCartPayload(payload) {
    return (
      payload &&
      typeof payload === 'object' &&
      (Array.isArray(payload.items) || Array.isArray(payload.cart_level_discount_applications))
    );
  }

  function getDiscountCount(cart) {
    const udbState = window.UdbDiscountApplyGuard?.evaluateUdbTierWin?.(cart);
    if (udbState?.isActive) {
      return 1;
    }

    const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
    const applicableCodes = codes.filter(
      (entry) => entry && entry.applicable !== false && String(entry.code || '').trim(),
    );

    if (applicableCodes.length) {
      return applicableCodes.length;
    }

    const applications = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];

    return applications.filter((app) => app && app.type === 'discount_code').length;
  }

  function getItemCount(cart) {
    const count = Number(cart?.item_count);
    if (Number.isFinite(count) && count >= 0) {
      return count;
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.reduce((sum, item) => sum + Math.max(0, Number(item?.quantity) || 0), 0);
  }

  function isSvgOnlyNode(node) {
    if (!node || node.nodeType !== 1) return false;
    const tag = node.tagName.toLowerCase();
    if (tag === 'svg') return true;
    return node.children.length === 1 && node.querySelector(':scope > svg') && !readRawLabelText(node);
  }

  function readRawLabelText(node) {
    if (!node) return '';

    return Array.from(node.childNodes)
      .filter((child) => child.nodeType === Node.TEXT_NODE)
      .map((child) => child.textContent)
      .join('')
      .trim();
  }

  function getLabelTextContainer(element) {
    const textChild = Array.from(element.children).find((child) => {
      const tag = child.tagName;
      return tag && tag.toLowerCase() !== 'svg' && !child.querySelector('svg');
    });

    return textChild || element;
  }

  function readElementText(element) {
    if (!element) return '';

    const container = getLabelTextContainer(element);
    if (container !== element) {
      return String(container.textContent || '').trim();
    }

    const raw = readRawLabelText(element);
    if (raw) return raw;

    return String(element.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function writeElementText(element, nextText) {
    if (!element) return;

    const container = getLabelTextContainer(element);

    if (container !== element) {
      container.textContent = nextText;
      return;
    }

    const directTextNodes = Array.from(element.childNodes).filter(
      (node) => node.nodeType === Node.TEXT_NODE,
    );
    const primaryTextNode =
      directTextNodes.find((node) => node.textContent.trim()) || directTextNodes[0];

    if (primaryTextNode) {
      primaryTextNode.textContent = nextText;
      directTextNodes.forEach((node) => {
        if (node !== primaryTextNode) {
          node.remove();
        }
      });
      return;
    }

    const firstSvg = element.querySelector(':scope > svg');
    const textNode = document.createTextNode(nextText);

    if (firstSvg && firstSvg.parentNode === element) {
      element.insertBefore(textNode, firstSvg.nextSibling);
      return;
    }

    element.insertBefore(textNode, element.firstChild);
  }

  function resolveDiscountLabelTarget(anchor) {
    if (!anchor) return null;

    const candidates = [];

    function addCandidate(node) {
      if (!node || candidates.includes(node) || isSvgOnlyNode(node)) return;
      candidates.push(node);
    }

    addCandidate(getLabelTextContainer(anchor));

    if (anchor.nextElementSibling) addCandidate(anchor.nextElementSibling);
    if (anchor.previousElementSibling) addCandidate(anchor.previousElementSibling);

    const parent = anchor.parentElement;
    if (parent) {
      Array.from(parent.children).forEach((child) => {
        if (child === anchor || isSvgOnlyNode(child)) return;
        const text = String(child.textContent || '').trim();
        if (!text) return;
        if (/(discount|rabatt)/i.test(text) || /\(\d+\)/.test(text)) {
          addCandidate(child);
        }
      });
    }

    const footer = anchor.closest(FOOTER_OPTIONS_SELECTOR);
    if (footer) {
      footer.querySelectorAll('span, p, div, button').forEach((node) => {
        if (node === anchor || anchor.contains(node) || node.contains(anchor)) return;
        const text = String(node.textContent || '').trim();
        if (!text || node.querySelector('svg')) return;
        if (/(discount|rabatt)/i.test(text)) {
          addCandidate(node);
        }
      });
    }

    const withCount = candidates.find((node) => /\(\d+\)\s*$/.test(readElementText(node)));
    if (withCount) return withCount;

    const withLabel = candidates.find((node) => /(discount|rabatt)/i.test(readElementText(node)));
    if (withLabel) return withLabel;

    return getLabelTextContainer(anchor);
  }

  function readDiscountLabelText(element) {
    const target = resolveDiscountLabelTarget(element);
    if (!target) return '';
    if (target !== element) return String(target.textContent || '').trim();
    return readElementText(element);
  }

  function parseDisplayedCount(element, readText) {
    const match = readText(element).match(/\((\d+)\)\s*$/);
    return match ? Number(match[1]) : 0;
  }

  function readLabelPrefix(element, readText, fallback) {
    const current = readText(element);
    return current.replace(/\s*\(\d+\)\s*$/, '').trim() || fallback;
  }

  function renderDiscountLabel(count) {
    const element = getDiscountElement();
    if (!element) return false;

    const prefix = readLabelPrefix(element, readDiscountLabelText, 'Discount');
    const next = count > 0 ? `${prefix} (${count})` : prefix;
    const current = readDiscountLabelText(element);

    if (current === next && lastKnownDiscountCount === count) {
      return false;
    }

    isUpdatingDiscount = true;
    writeElementText(element, next);
    element.hidden = false;
    element.style.removeProperty('display');
    element.removeAttribute('aria-hidden');
    lastKnownDiscountCount = count;

    window.requestAnimationFrame(() => {
      isUpdatingDiscount = false;
    });

    return true;
  }

  function renderHeaderLabel(itemCount) {
    const element = getHeaderElement();
    if (!element) return false;

    const prefix = readLabelPrefix(element, readElementText, 'Shopping cart');
    const next = `${prefix} (${itemCount})`;
    const current = readElementText(element);

    if (current === next && lastKnownItemCount === itemCount) {
      return false;
    }

    isUpdatingHeader = true;
    writeElementText(element, next);
    lastKnownItemCount = itemCount;

    window.requestAnimationFrame(() => {
      isUpdatingHeader = false;
    });

    return true;
  }

  function shouldRefreshDiscountLabel(count) {
    if (count !== lastKnownDiscountCount) return true;

    const element = getDiscountElement();
    if (!element) return false;

    const displayedCount = parseDisplayedCount(element, readDiscountLabelText);
    const currentText = readDiscountLabelText(element);

    if (count === 0) {
      return displayedCount > 0 || /\(\d+\)\s*$/.test(currentText);
    }

    return displayedCount !== count || !currentText.includes(`(${count})`);
  }

  function shouldRefreshHeaderLabel(itemCount) {
    if (itemCount !== lastKnownItemCount) return true;

    const element = getHeaderElement();
    if (!element) return false;

    return parseDisplayedCount(element, readElementText) !== itemCount;
  }

  function syncHeaderFromCart(cart, force) {
    const itemCount = getItemCount(cart);

    if (!force && !shouldRefreshHeaderLabel(itemCount)) {
      lastKnownItemCount = itemCount;
      return false;
    }

    return renderHeaderLabel(itemCount);
  }

  function syncDiscountFromCart(cart, force) {
    const count = getDiscountCount(cart);

    if (!force && !shouldRefreshDiscountLabel(count)) {
      lastKnownDiscountCount = count;
      return false;
    }

    return renderDiscountLabel(count);
  }

  function applyCartLabels(cart, options) {
    const force = Boolean(options && options.force);

    if (!isCartPayload(cart)) {
      return false;
    }

    const normalizedCart = normalizeCartPayload(cart);

    const headerChanged = syncHeaderFromCart(normalizedCart, force);
    verifyHeaderOnce();
    const discountChanged = syncDiscountFromCart(normalizedCart, force);

    return headerChanged || discountChanged;
  }

  function syncFromCart(cart, force) {
    return applyCartLabels(cart, { force });
  }

  function verifyHeaderOnce() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const element = getHeaderElement();
        if (!element) return;

        const displayed = parseDisplayedCount(element, readElementText);
        if (lastKnownItemCount != null && displayed === lastKnownItemCount) {
          return;
        }

        fetchCartOnce()
          .then((cart) => {
            syncHeaderFromCart(cart, true);
          })
          .catch(() => {
            if (lastKnownItemCount != null && displayed !== lastKnownItemCount) {
              renderHeaderLabel(lastKnownItemCount);
            }
          });
      });
    });
  }

  function forceSyncFromServer() {
    return fetchCartOnce()
      .then((cart) => {
        applyCartLabels(cart, { force: true });
        return cart;
      })
      .catch(() => null);
  }

  function fetchCartOnce() {
    if (inFlightCartRequest) {
      return inFlightCartRequest;
    }

    inFlightCartRequest = fetch(`${getRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to fetch cart');
        }
        return response.json();
      })
      .finally(() => {
        inFlightCartRequest = null;
      });

    return inFlightCartRequest;
  }

  function isAwaitingManualDiscount() {
    return Boolean(window.SbbCouponFlow?.isAwaitingManualDiscount?.() || window.__sbbAwaitingManualDiscount);
  }

  function requestServerSync(force) {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      fetchCartOnce()
        .then((cart) => {
          applyCartLabels(cart, { force });
        })
        .catch(() => {
          // ignore
        });
    }, DEBOUNCE_MS);
  }

  function scheduleDiscountVerifySync() {
    discountVerifyTimers.forEach((timer) => window.clearTimeout(timer));
    discountVerifyTimers = DISCOUNT_VERIFY_DELAYS_MS.map((delay) =>
      window.setTimeout(() => {
        fetchCartOnce()
          .then((cart) => {
            if (!isCartPayload(cart)) return;
            syncDiscountFromCart(cart, true);
          })
          .catch(() => {
            // ignore
          });
      }, delay),
    );
  }

  function requestTouchesDiscount(body, url) {
    const urlText = String(url || '');

    if (DISCOUNT_REQUEST_PATTERN.test(urlText)) {
      return true;
    }

    if (!body) {
      return false;
    }

    if (typeof body === 'string') {
      return /"discount"\s*:|discount=|discount%5B|discount_code/i.test(body);
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return Array.from(body.keys()).some((key) => DISCOUNT_REQUEST_PATTERN.test(key));
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return Array.from(body.keys()).some((key) => DISCOUNT_REQUEST_PATTERN.test(key));
    }

    return false;
  }

  function tryCompleteManualDiscountApply(payload, touchedDiscount) {
    if (!touchedDiscount || !isAwaitingManualDiscount()) {
      return;
    }

    if (!isCartPayload(payload)) {
      return;
    }

    const attemptedCode =
      window.__udbLastAttemptedDiscountCode ||
      (Array.isArray(payload.discount_codes)
        ? String(payload.discount_codes[payload.discount_codes.length - 1]?.code || '').trim()
        : '');

    if (window.UdbAovDiscountNotice?.inspectCartRejection?.(payload, attemptedCode)) {
      return;
    }

    const count = getDiscountCount(payload);
    const codes = Array.isArray(payload.discount_codes) ? payload.discount_codes : [];
    const hasApplicableCode = codes.some(
      (entry) => entry && entry.applicable !== false && String(entry.code || '').trim(),
    );

    if (count > 0 || hasApplicableCode) {
      window.SbbCouponFlow?.completeManualDiscountApply?.(payload);
    }
  }

  function notifyDiscountCartChange(payload, touchedDiscount) {
    if (!touchedDiscount || !isCartPayload(payload)) {
      return;
    }

    window.UdbAovDiscountNotice?.handleCartDiscountResult?.(payload);
  }

  function normalizeCartPayload(cart) {
    if (!isCartPayload(cart)) {
      return cart;
    }

    return window.UdbDiscountApplyGuard?.normalizeCartForUdbTier?.(cart) || cart;
  }

  function handleCartPayload(payload, verifyLater) {
    if (isCartPayload(payload)) {
      const cart = normalizeCartPayload(payload);
      tryCompleteManualDiscountApply(cart, verifyLater);
      notifyDiscountCartChange(cart, verifyLater);
      window.UdbAovDiscountUi?.syncFromCart?.(cart);
      applyCartLabels(cart, { force: true });
      if (verifyLater) {
        scheduleDiscountVerifySync();
      }
      return;
    }

    requestServerSync(true);
  }

  function isAovCartFetchRequest(method, url) {
    return String(method || 'GET').toUpperCase() === 'GET' && AOV_CART_FETCH_PATTERN.test(String(url || ''));
  }

  function isCartMutationRequest(method, url) {
    return String(method || 'GET').toUpperCase() === 'POST' && CART_MUTATION_PATTERN.test(String(url || ''));
  }

  function buildNormalizedCartResponse(response, cart) {
    const normalized = normalizeCartPayload(cart);
    if (normalized === cart) {
      return response;
    }

    const headers = new Headers(response.headers);
    headers.set('Content-Type', 'application/json; charset=utf-8');

    return new Response(JSON.stringify(normalized), {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  }

  function shouldNormalizeCartResponse(method, url) {
    const normalizedMethod = String(method || 'GET').toUpperCase();
    const normalizedUrl = String(url || '');

    return (
      isCartMutationRequest(normalizedMethod, normalizedUrl) ||
      isAovCartFetchRequest(normalizedMethod, normalizedUrl) ||
      (normalizedMethod === 'GET' && /\/cart\.js(?:\?|$)/i.test(normalizedUrl))
    );
  }

  function handleCartResponse(method, url, payload, body) {
    const cart = normalizeCartPayload(payload);

    if (isCartMutationRequest(method, url)) {
      const touchedDiscount = requestTouchesDiscount(body, url);
      handleCartPayload(cart, touchedDiscount);
      return cart;
    }

    if (shouldNormalizeCartResponse(method, url) && isCartPayload(cart)) {
      if (isAwaitingManualDiscount()) {
        window.SbbCouponFlow?.completeManualDiscountApply?.(cart);
      }
      applyCartLabels(cart, { force: true });
      window.UdbAovDiscountUi?.syncFromCart?.(cart);
    }

    return cart;
  }

  function patchFetchForSync() {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async function patchedFetchForAovCartLabels(input, init) {
      const response = await originalFetch(input, init);
      const url = String(typeof input === 'string' ? input : input?.url || '');
      const method = String(init?.method || 'GET');

      if (!shouldNormalizeCartResponse(method, url) || !response.ok) {
        return response;
      }

      try {
        const payload = await response.clone().json();
        const cart = handleCartResponse(method, url, payload, init?.body);
        return buildNormalizedCartResponse(response, cart);
      } catch (error) {
        requestServerSync(true);
      }

      return response;
    };
  }

  function patchXhrForSync() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function open(method, url, ...args) {
      this.__aovCartLabelSyncMethod = method;
      this.__aovCartLabelSyncUrl = url;
      return originalOpen.call(this, method, url, ...args);
    };

    XMLHttpRequest.prototype.send = function send(body, ...args) {
      this.addEventListener('load', function onCartXhrLoad() {
        if (this.status < 200 || this.status >= 300) {
          return;
        }

        const method = this.__aovCartLabelSyncMethod;
        const url = this.__aovCartLabelSyncUrl;

        if (!shouldNormalizeCartResponse(method, url)) {
          return;
        }

        try {
          const payload = JSON.parse(this.responseText);
          const cart = handleCartResponse(method, url, payload, body);
          const normalized = normalizeCartPayload(cart);
          if (normalized !== payload) {
            const json = JSON.stringify(normalized);
            Object.defineProperty(this, 'responseText', { value: json, configurable: true });
            Object.defineProperty(this, 'response', { value: json, configurable: true });
          }
        } catch (error) {
          requestServerSync(true);
        }
      });

      return originalSend.call(this, body, ...args);
    };
  }

  function watchDiscountElement(element) {
    if (!element || element.dataset.aovDiscountLabelWatch) return;

    element.dataset.aovDiscountLabelWatch = 'true';

    new MutationObserver(() => {
      if (isUpdatingDiscount) return;
      requestServerSync(true);
      scheduleDiscountVerifySync();
    }).observe(element, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function watchHeaderElement(element) {
    if (!element || element.dataset.aovHeaderLabelWatch) return;

    element.dataset.aovHeaderLabelWatch = 'true';

    new MutationObserver(() => {
      if (isUpdatingHeader) return;

      if (lastKnownItemCount != null) {
        verifyHeaderOnce();
        return;
      }

      requestServerSync(true);
    }).observe(element, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function bindCartEvents() {
    document.addEventListener('cart:change', (event) => {
      const cart = event?.detail?.cart;
      if (isCartPayload(cart)) {
        tryCompleteManualDiscountApply(cart, true);
        notifyDiscountCartChange(cart, true);
        handleCartPayload(cart, false);
        return;
      }

      requestServerSync(true);
    });

    document.documentElement.addEventListener('cart:refresh', () => {
      requestServerSync(true);
    });

    document.addEventListener('sbb:coupon-flow-end', () => {
      requestServerSync(true);
      scheduleDiscountVerifySync();
    });

    ['CART_CHANGED', 'ITEM_ADDED_TO_CART', 'ITEM_REMOVED'].forEach((name) => {
      window.addEventListener(`aov:${name}`, () => {
        requestServerSync(true);
      });
    });

    window.addEventListener('aov:discount-count-sync', () => {
      requestServerSync(true);
      scheduleDiscountVerifySync();
    });

    window.addEventListener('aov:cart-count-sync', () => {
      requestServerSync(true);
    });

    document.addEventListener('variant:add', () => {
      requestServerSync(true);
    });
  }

  function bindAovDrawerInteractions(drawer) {
    if (!drawer || drawer.dataset.aovCartLabelInteractionWatch) return;
    drawer.dataset.aovCartLabelInteractionWatch = 'true';

    drawer.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const isCartChange = Boolean(
          target.closest('[class*="Quantity"]') ||
            target.closest('[class*="quantity"]') ||
            target.closest('[class*="LineItem"]') ||
            target.closest('[class*="line-item"]') ||
            target.closest('button[aria-label*="remove" i]') ||
            target.closest('button[aria-label*="entfernen" i]'),
        );

        if (isCartChange) {
          requestServerSync(true);
          return;
        }

        const button = target.closest('button');
        if (!button) return;

        const label = String(button.textContent || '').trim();
        if (
          /apply/i.test(label) ||
          /anwenden/i.test(label) ||
          button.closest('[class*="Discount"]')
        ) {
          requestServerSync(true);
          scheduleDiscountVerifySync();
        }
      },
      true,
    );
  }

  function onCartElementsReady() {
    const discountElement = getDiscountElement();
    const headerElement = getHeaderElement();

    if (discountElement) watchDiscountElement(discountElement);
    if (headerElement) watchHeaderElement(headerElement);

    if (discountElement || headerElement) {
      requestServerSync(true);
    }
  }

  function watchDrawerOpenState(drawer) {
    if (!drawer || drawer.dataset.aovDrawerOpenWatch) return;
    drawer.dataset.aovDrawerOpenWatch = 'true';

    let wasOpen = false;

    new MutationObserver(() => {
      const isOpen =
        drawer.classList.contains('is-open') ||
        drawer.classList.contains('open') ||
        drawer.getAttribute('aria-hidden') === 'false' ||
        drawer.hasAttribute('open');

      if (isOpen && !wasOpen) {
        onCartElementsReady();
        forceSyncFromServer();
      }

      wasOpen = isOpen;
    }).observe(drawer, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'open'],
    });
  }

  function watchAovDrawer() {
    const drawer = document.getElementById('aov-cart-drawer');
    if (!drawer) return;

    if (!drawer.dataset.aovCartLabelWatch) {
      drawer.dataset.aovCartLabelWatch = 'true';
      bindAovDrawerInteractions(drawer);
      watchDrawerOpenState(drawer);
    }

    onCartElementsReady();
  }

  function init() {
    patchFetchForSync();
    patchXhrForSync();
    bindCartEvents();
    watchAovDrawer();

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          if (node.id === 'aov-cart-drawer' || (node.querySelector && node.querySelector('#aov-cart-drawer'))) {
            watchAovDrawer();
          }

          const matchesCartUi =
            (node.matches &&
              (node.matches(DISCOUNT_TARGET_SELECTOR) || node.matches(HEADER_TARGET_SELECTOR))) ||
            (node.querySelector &&
              (node.querySelector(DISCOUNT_TARGET_SELECTOR) || node.querySelector(HEADER_TARGET_SELECTOR)));

          if (matchesCartUi) {
            onCartElementsReady();
          }
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const api = {
    getDiscountCount,
    getItemCount,
    applyCartLabels,
    syncFromCart,
    requestServerSync,
    forceSyncFromServer,
    fetchCartOnce,
    getDiscountElement,
    getHeaderElement,
    findAovLabel,
    readDiscountLabelText,
    readElementText,
    writeElementText,
    resolveDiscountLabelTarget,
  };

  window.AovCartLabelSync = api;
  window.AovCartCountSync = api;
  window.AovDiscountCountSync = api;
})();
